import type { ContactOrderAppointmentRow } from "../../../../../_logic/contact-orders-contract"
import {
  defaultEnabledChannelsForKind,
  enabledChannelsForContactPoint,
  normalizeEnabledChannelsForKind,
} from "../../../../../_logic/contact-channels"
import type {
  QuickRecipientContactRow,
  QuickRecipientSavePlan,
} from "../../../_shared/_logic/recipient-quick-edit-contract"

const normalizeText = (value: unknown) => `${value ?? ""}`.trim()
const normalizeKind = (value: unknown) => normalizeText(value).toUpperCase()

const createClientId = (prefix: string) =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? `${prefix}:${crypto.randomUUID()}`
    : `${prefix}:${Date.now()}:${Math.random().toString(36).slice(2)}`

const activeContactPoints = (contactPoints: ContactPoint[]) =>
  contactPoints.filter((point) => {
    const status = normalizeKind(point.lifecycle_status || "ACTIVE")

    return !["INVALID", "DO_NOT_USE", "ARCHIVED"].includes(status)
  })

type EditableContactPointKind = "EMAIL" | "MOBILE" | "FIXED_LINE"

const firstContactPointForKind = (
  customerId: string,
  contactPoints: ContactPoint[],
  kind: EditableContactPointKind,
) =>
  activeContactPoints(contactPoints).find(
    (point) => point.customer_id === customerId && normalizeKind(point.kind) === kind,
  ) ?? null

const enabledChannelsForKind = (kind: EditableContactPointKind, point: ContactPoint | null) =>
  point ? enabledChannelsForContactPoint(point) : defaultEnabledChannelsForKind(kind)

const sameChannelSelection = (
  kind: EditableContactPointKind,
  left: readonly ContactChannel[],
  right: readonly ContactChannel[],
) => {
  const normalizedLeft = normalizeEnabledChannelsForKind(kind, left)
  const normalizedRight = normalizeEnabledChannelsForKind(kind, right)

  if (normalizedLeft.length !== normalizedRight.length) return false

  const rightSet = new Set(normalizedRight)

  return normalizedLeft.every((channel) => rightSet.has(channel))
}

export const recipientRowsForContactOrder = ({
  row,
  customersById,
  contactPoints,
}: {
  row: ContactOrderAppointmentRow
  customersById: Record<string, Customer>
  contactPoints: ContactPoint[]
}): QuickRecipientContactRow[] =>
  row.recipients.map((recipient) => {
    const customer = customersById[recipient.customerId] ?? null
    const email = firstContactPointForKind(recipient.customerId, contactPoints, "EMAIL")
    const mobile = firstContactPointForKind(recipient.customerId, contactPoints, "MOBILE")
    const fixedLine = firstContactPointForKind(recipient.customerId, contactPoints, "FIXED_LINE")
    const name = normalizeText(customer?.display_name || recipient.nameLabel || recipient.tag.label)
    const emailValue = normalizeText(email?.value)
    const mobileValue = normalizeText(mobile?.value)
    const fixedLineValue = normalizeText(fixedLine?.value)
    const emailEnabledChannels = enabledChannelsForKind("EMAIL", email)
    const mobileEnabledChannels = enabledChannelsForKind("MOBILE", mobile)
    const fixedLineEnabledChannels = enabledChannelsForKind("FIXED_LINE", fixedLine)

    return {
      clientId: recipient.customerId,
      customerId: recipient.customerId,
      emailContactPointId: email?.id ?? null,
      mobileContactPointId: mobile?.id ?? null,
      fixedLineContactPointId: fixedLine?.id ?? null,
      name,
      email: emailValue,
      emailEnabledChannels,
      mobile: mobileValue,
      mobileEnabledChannels,
      fixedLine: fixedLineValue,
      fixedLineEnabledChannels,
      originalName: name,
      originalEmail: emailValue,
      originalEmailEnabledChannels: [...emailEnabledChannels],
      originalMobile: mobileValue,
      originalMobileEnabledChannels: [...mobileEnabledChannels],
      originalFixedLine: fixedLineValue,
      originalFixedLineEnabledChannels: [...fixedLineEnabledChannels],
      isNew: false,
    }
  })

export const createBlankRecipientRow = (): QuickRecipientContactRow => ({
  clientId: createClientId("new-recipient"),
  name: "",
  email: "",
  emailEnabledChannels: defaultEnabledChannelsForKind("EMAIL"),
  mobile: "",
  mobileEnabledChannels: defaultEnabledChannelsForKind("MOBILE"),
  fixedLine: "",
  fixedLineEnabledChannels: defaultEnabledChannelsForKind("FIXED_LINE"),
  isNew: true,
})

export const normalizeQuickRecipientRow = (
  row: QuickRecipientContactRow,
): QuickRecipientContactRow => ({
  ...row,
  name: normalizeText(row.name),
  email: normalizeText(row.email),
  emailEnabledChannels: normalizeEnabledChannelsForKind(
    "EMAIL",
    row.emailEnabledChannels ?? defaultEnabledChannelsForKind("EMAIL"),
  ),
  mobile: normalizeText(row.mobile),
  mobileEnabledChannels: normalizeEnabledChannelsForKind(
    "MOBILE",
    row.mobileEnabledChannels ?? defaultEnabledChannelsForKind("MOBILE"),
  ),
  fixedLine: normalizeText(row.fixedLine),
  fixedLineEnabledChannels: normalizeEnabledChannelsForKind(
    "FIXED_LINE",
    row.fixedLineEnabledChannels ?? defaultEnabledChannelsForKind("FIXED_LINE"),
  ),
  originalName: row.originalName == null ? row.originalName : normalizeText(row.originalName),
  originalEmail: row.originalEmail == null ? row.originalEmail : normalizeText(row.originalEmail),
  originalEmailEnabledChannels:
    row.originalEmailEnabledChannels == null
      ? row.originalEmailEnabledChannels
      : normalizeEnabledChannelsForKind("EMAIL", row.originalEmailEnabledChannels),
  originalMobile:
    row.originalMobile == null ? row.originalMobile : normalizeText(row.originalMobile),
  originalMobileEnabledChannels:
    row.originalMobileEnabledChannels == null
      ? row.originalMobileEnabledChannels
      : normalizeEnabledChannelsForKind("MOBILE", row.originalMobileEnabledChannels),
  originalFixedLine:
    row.originalFixedLine == null ? row.originalFixedLine : normalizeText(row.originalFixedLine),
  originalFixedLineEnabledChannels:
    row.originalFixedLineEnabledChannels == null
      ? row.originalFixedLineEnabledChannels
      : normalizeEnabledChannelsForKind("FIXED_LINE", row.originalFixedLineEnabledChannels),
})

export const quickRecipientEmailError = (value: string): string | null => {
  const normalized = normalizeText(value)

  if (!normalized) return null

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)
    ? null
    : "Bitte eine gültige E-Mail-Adresse angeben."
}

export const quickRecipientPhoneError = (value: string): string | null => {
  const normalized = normalizeText(value)

  if (!normalized) return null

  if (!/^[\d+().\s-]+$/.test(normalized)) return "Bitte eine gültige Telefonnummer angeben."

  const digitsOnly = normalized.replace(/\D/g, "")

  return digitsOnly.length >= 3 ? null : "Bitte eine gültige Telefonnummer angeben."
}

export const quickRecipientRowValidationError = (row: QuickRecipientContactRow): string | null => {
  const normalized = normalizeQuickRecipientRow(row)

  if (!normalized.name) return "Bitte einen Namen angeben."

  return (
    quickRecipientEmailError(normalized.email) ??
    quickRecipientPhoneError(normalized.mobile) ??
    quickRecipientPhoneError(normalized.fixedLine)
  )
}

export const hasQuickRecipientRowChanges = (row: QuickRecipientContactRow): boolean => {
  const normalized = normalizeQuickRecipientRow(row)

  if (normalized.isNew)
    return Boolean(normalized.name || normalized.email || normalized.mobile || normalized.fixedLine)

  return (
    normalized.name !== normalizeText(normalized.originalName) ||
    normalized.email !== normalizeText(normalized.originalEmail) ||
    (Boolean(normalized.email) &&
      !sameChannelSelection(
        "EMAIL",
        normalized.emailEnabledChannels,
        normalized.originalEmailEnabledChannels ?? defaultEnabledChannelsForKind("EMAIL"),
      )) ||
    normalized.mobile !== normalizeText(normalized.originalMobile) ||
    (Boolean(normalized.mobile) &&
      !sameChannelSelection(
        "MOBILE",
        normalized.mobileEnabledChannels,
        normalized.originalMobileEnabledChannels ?? defaultEnabledChannelsForKind("MOBILE"),
      )) ||
    normalized.fixedLine !== normalizeText(normalized.originalFixedLine) ||
    (Boolean(normalized.fixedLine) &&
      !sameChannelSelection(
        "FIXED_LINE",
        normalized.fixedLineEnabledChannels,
        normalized.originalFixedLineEnabledChannels ?? defaultEnabledChannelsForKind("FIXED_LINE"),
      ))
  )
}

export const buildQuickRecipientSavePlan = (
  rows: QuickRecipientContactRow[],
): QuickRecipientSavePlan => {
  const changedRows = rows.map(normalizeQuickRecipientRow).filter(hasQuickRecipientRowChanges)

  return {
    existingRows: changedRows.filter((row) => !row.isNew && Boolean(row.customerId)),
    newRows: changedRows.filter((row) => row.isNew && Boolean(row.name)),
  }
}

export const quickRecipientRowsHaveValidationErrors = (rows: QuickRecipientContactRow[]) =>
  rows.some(
    (row) => hasQuickRecipientRowChanges(row) && Boolean(quickRecipientRowValidationError(row)),
  )

export const quickRecipientRowsHaveChanges = (rows: QuickRecipientContactRow[]) =>
  rows.some(hasQuickRecipientRowChanges)
